import { useEffect, useState } from "react";
import { Sparkles, MessageSquareText, Send, Mail, Loader2, ShieldQuestion } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";

const SAMPLE_QS = [
  "What's involved in a perimeter drainage replacement on an older Victoria home?",
  "How long does a typical residential foundation excavation take?",
  "Do I need a permit to pour a new concrete driveway in Langford?",
  "What's the difference between stamped and exposed-aggregate concrete?",
];

export default function QA() {
  usePageTitle("Ask Char — Q&A", "Ask our AI-powered assistant anything about excavation, concrete, drainage or site prep in BC — instant answers backed by the AV Contracting crew.");
  const [question, setQuestion] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(null); // {answer, escalated}
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadHistory = async () => {
    try {
      const r = await api.get("/qa");
      setHistory(r.data || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const ask = async (e) => {
    e?.preventDefault();
    if (!question.trim()) return;
    setBusy(true);
    setError("");
    setLatest(null);
    try {
      const { data } = await api.post("/qa/ask", {
        question: question.trim(),
        name: name.trim() || null,
        email: email.trim() || null,
      });
      setLatest(data);
      if (!data.escalated) loadHistory();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-20 md:pt-24" data-testid="qa-page">
      <section className="bg-[#2C3539] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase mb-3 inline-flex items-center gap-2">
            <Sparkles size={14}/> AI-Powered Q&A
          </div>
          <h1 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.92]">Ask Char<br/>anything.</h1>
          <p className="mt-6 max-w-2xl text-white/80 text-base md:text-lg">
            Quick answers powered by AI on excavation, concrete, drainage and site work — trained on AV Contracting's expertise. Stumpers get sent straight to Char for a personal response.
          </p>
        </div>
      </section>

      {/* Ask Form */}
      <section className="bg-white">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-20">
          <form onSubmit={ask} className="bg-[#F1F3F5] border-2 border-[#2C3539]/10 p-6 md:p-10" data-testid="qa-form">
            <label className="block">
              <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-3">Your Question *</span>
              <textarea
                data-testid="qa-question"
                rows={4}
                required
                minLength={4}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="e.g. How deep should perimeter drainage sit around a 1970s foundation?"
                className="w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3 text-[#2C3539] text-base font-medium resize-y"
              />
            </label>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block">
                <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">Your Name (optional)</span>
                <input
                  data-testid="qa-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3"
                />
              </label>
              <label className="block">
                <span className="block text-xs tracking-[0.2em] font-bold uppercase text-[#2C3539]/80 mb-2">Email (so Char can reply if needed)</span>
                <input
                  data-testid="qa-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border-2 border-[#2C3539]/15 focus:border-[#2F5F80] focus:outline-none px-4 py-3"
                />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-[#2C3539]/60">Free · instant AI answer · escalated to Char if it's a tough one.</div>
              <button
                type="submit"
                disabled={busy}
                data-testid="qa-submit"
                className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-7 py-3.5 uppercase transition-colors disabled:opacity-50"
              >
                {busy ? <><Loader2 size={16} className="animate-spin"/> Thinking…</> : <><Send size={16}/> Ask</>}
              </button>
            </div>

            {/* Sample questions */}
            {!latest && (
              <div className="mt-6 pt-6 border-t border-[#2C3539]/10" data-testid="qa-sample-questions">
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#2C3539]/60 mb-3">Try one of these</div>
                <div className="flex flex-wrap gap-2">
                  {SAMPLE_QS.map((q) => (
                    <button
                      type="button"
                      key={q}
                      onClick={() => setQuestion(q)}
                      className="bg-white border border-[#2C3539]/15 hover:border-[#2F5F80] hover:text-[#2F5F80] text-[#2C3539] text-xs px-3 py-2 transition-colors text-left max-w-md"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Result */}
            {error && (
              <div className="mt-6 bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-700" data-testid="qa-error">{error}</div>
            )}
            {latest && (
              <div className="mt-6" data-testid="qa-result">
                {latest.escalated ? (
                  <div className="bg-[#2C3539] text-white p-6 border-l-4 border-[#2F5F80]" data-testid="qa-escalated">
                    <div className="inline-flex items-center gap-2 text-[#2F5F80] text-xs tracking-[0.3em] uppercase font-bold mb-3">
                      <Mail size={14}/> Forwarded to Char
                    </div>
                    <p className="text-white/90 leading-relaxed">{latest.answer}</p>
                    {email ? (
                      <p className="mt-3 text-xs text-white/60">We'll reply to <span className="text-white">{email}</span> directly.</p>
                    ) : (
                      <p className="mt-3 text-xs text-white/60">For a personal reply, please share your email and ask again.</p>
                    )}
                  </div>
                ) : (
                  <div className="bg-white border-2 border-[#2F5F80] p-6" data-testid="qa-answer">
                    <div className="inline-flex items-center gap-2 text-[#2F5F80] text-xs tracking-[0.3em] uppercase font-bold mb-3">
                      <Sparkles size={14}/> AI Answer
                    </div>
                    <p className="text-[#2C3539] leading-relaxed whitespace-pre-line">{latest.answer}</p>
                    <p className="mt-4 text-[10px] tracking-widest uppercase text-[#2C3539]/40">Confidence: {latest.confidence}</p>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Public history */}
      <section className="bg-[#F1F3F5]">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-20">
          <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase mb-3">Common Questions</div>
          <h2 className="font-display text-4xl md:text-5xl uppercase leading-[1] text-[#2C3539]">Recently<br/>answered.</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="qa-history">
            {historyLoading ? (
              <div className="font-display text-2xl text-[#2C3539]/50">LOADING…</div>
            ) : history.length === 0 ? (
              <div className="md:col-span-2 bg-white border-2 border-dashed border-[#2C3539]/20 p-10 text-center">
                <ShieldQuestion size={36} className="text-[#2F5F80] mx-auto"/>
                <div className="mt-3 font-display text-xl uppercase text-[#2C3539]">No questions answered yet</div>
                <p className="text-sm text-[#2C3539]/60">Be the first to ask — public answers will appear here.</p>
              </div>
            ) : (
              history.map((h) => (
                <article key={h.id} className="bg-white border-2 border-[#2C3539]/10 hover:border-[#2F5F80] transition-colors p-6" data-testid={`qa-history-${h.id}`}>
                  <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-[#2F5F80]"><MessageSquareText size={12}/> Q&A</div>
                  <div className="mt-2 font-display text-lg md:text-xl uppercase text-[#2C3539] leading-tight">{h.question}</div>
                  <p className="mt-3 text-sm text-[#2C3539]/80 leading-relaxed whitespace-pre-line">{h.answer}</p>
                </article>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
